import { useState } from 'react';

const TermsOfService = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      content: 'By downloading, installing, or using Sentivo, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our software.'
    },
    {
      id: 'description',
      title: 'Service Description',
      content: 'Sentivo is a local search application for macOS that helps users find files and information across their Mac devices. The software operates entirely on your local device and does not require internet connectivity for core functionality.'
    },
    {
      id: 'license',
      title: 'License Grant',
      content: 'We grant you a limited, non-exclusive, non-transferable license to use Sentivo for personal or commercial purposes in accordance with these terms. This license is revocable at any time for violation of these terms.'
    },
    {
      id: 'permitted-use',
      title: 'Permitted Use',
      content: 'You may use Sentivo to search and organize files on your Mac device. You may install Sentivo on multiple Mac computers that you own or control. Commercial use is permitted under the same license terms.'
    },
    {
      id: 'restrictions',
      title: 'Usage Restrictions',
      content: 'You may not reverse engineer, decompile, or disassemble Sentivo. You may not redistribute, sell, or sublicense the software. You may not use Sentivo for illegal purposes or to violate the rights of others.'
    },
    {
      id: 'privacy',
      title: 'Privacy and Data',
      content: 'Sentivo processes your data locally on your device. We do not collect, store, or transmit your personal files or search queries to external servers. For detailed information about data handling, please refer to our Privacy Policy.'
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      content: 'Sentivo and all related intellectual property rights are owned by us. This license does not grant you any rights to our trademarks, service marks, or other proprietary rights.'
    },
    {
      id: 'disclaimer',
      title: 'Disclaimer of Warranties',
      content: 'Sentivo is provided "as is" without warranties of any kind, either express or implied. We do not guarantee that the software will be error-free or uninterrupted. We are not responsible for data loss or system damage.'
    },
    {
      id: 'limitation',
      title: 'Limitation of Liability',
      content: 'To the maximum extent permitted by law, our liability for any damages arising from your use of Sentivo shall not exceed the amount you paid for the software. We are not liable for indirect, incidental, or consequential damages.'
    },
    {
      id: 'termination',
      title: 'Termination',
      content: 'We may terminate your license to use Sentivo at any time if you violate these terms. Upon termination, you must cease all use of the software and uninstall it from your devices.'
    },
    {
      id: 'updates',
      title: 'Updates and Modifications',
      content: 'We may provide updates, modifications, or new versions of Sentivo. Your continued use of the software after updates constitutes acceptance of any modified terms.'
    },
    {
      id: 'governing-law',
      title: 'Governing Law',
      content: 'These terms are governed by the laws of the jurisdiction where our company is based, without regard to conflict of law principles. Any disputes will be resolved in the appropriate courts of that jurisdiction.'
    }
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            Last updated: 10/12/2025
          </p>
          
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-8">
            <p className="text-green-700">
              <strong>Important:</strong> Please read these terms carefully before using Sentivo. 
              By using our software, you agree to be bound by these terms and conditions.
            </p>
          </div>

          <div className="space-y-4">
            {sections.map((section) => (
              <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                >
                  <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 transform transition-transform ${
                      expandedSection === section.id ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {expandedSection === section.id && (
                  <div className="px-6 py-4 bg-white">
                    <p className="text-gray-600">{section.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h3>
            <p className="text-gray-600 mb-4">
              If you have any questions about these Terms of Service, please contact us at sentivo.contact@gmail.com.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-8">Changes to Terms</h3>
            <p className="text-gray-600">
              We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. 
              Your continued use of Sentivo after any changes constitutes acceptance of the modified terms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
